import { useState } from 'react';

// Single clean contact card — Mission Control style. The fields removed
// from the legacy form (Salutation, Middle Name, Greeting, Department,
// Division, Category, Account Manager) live on the contacts table for
// legacy data but the slim card no longer surfaces or writes them.
//
// Layout (in render order):
//   1. First Name + Last Name (one row)
//   2. Position
//   3. Company
//   4. Address 1 / Address 2
//   5. City + Province + Postal + Country
//   6. Phone Main / Phone Cell / Phone Fax
//   7. Email 1 / Email 2 / Email 3
//   8. Website
//
// last_contact / next_contact round-trip silently — the Notes workflow
// still updates them, the card doesn't render inputs for them.
// Two-state customer status: Active is the default, Inactive flips
// the customer (and via server cascade, all of their sites) so they
// drop out of renewal strips, month cards, and dashboards. Mirrors
// the LeadForm Status dropdown shape; this list is intentionally
// short -- customers have one of two lifecycle states, unlike leads
// which run through a sales funnel with many stops.
const STATUS_OPTIONS = ['Active', 'Inactive'];

const EMPTY = {
  first_name: '', last_name: '', position: '',
  company: '',
  address1: '', address2: '',
  city: '', province_state: '', postal_zip: '', country: 'Canada',
  phone_main: '', phone_cell: '', phone_fax: '',
  email1: '', email2: '', email3: '',
  website: '',
  last_contact: '', next_contact: '',
  first_contact_date: '',
  highlight_color: '',
  current_status: ''
};

function dateOnly(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

function normalizeInitial(initial) {
  if (!initial) return { ...EMPTY };
  const merged = { ...EMPTY };
  for (const key of Object.keys(EMPTY)) {
    if (initial[key] != null) merged[key] = initial[key];
  }
  for (const dateKey of ['last_contact', 'next_contact', 'first_contact_date']) {
    merged[dateKey] = dateOnly(merged[dateKey]);
  }
  return merged;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validate(form) {
  const errors = {};
  if (!form.last_name.trim()) errors.last_name = 'Last name is required.';
  for (const k of ['email1', 'email2', 'email3']) {
    if (form[k] && !EMAIL_RE.test(form[k])) errors[k] = 'Invalid email format.';
  }
  return errors;
}

export default function CustomerForm({ initial, onSubmit, submitLabel = 'Save', onCancel }) {
  const initialForm = normalizeInitial(initial);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);

  function update(field) {
    return (e) => {
      const v = e.target.value;
      setForm((prev) => ({ ...prev, [field]: v }));
      if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
    };
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitError(null);
    const found = validate(form);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    setSaving(true);
    try {
      await onSubmit(form);
    } catch (err) {
      setSubmitError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="contact-card-form" noValidate>
      {submitError && <p className="error">{submitError}</p>}

      <div className="contact-card">
        <div className="contact-card-grid">
          {/* Row 1 — Who & how to reach them (Dave 2026-06-26).
              Reordered by frequency of use: Name + Cell next to each
              other so the "dial this person" path is one glance, not
              a scan across 12 columns. */}
          <Field label="First Name" width={180}>
            <input value={form.first_name} onChange={update('first_name')} />
          </Field>
          <Field label="Last Name" width={180} required error={errors.last_name}>
            <input
              value={form.last_name}
              onChange={update('last_name')}
              aria-invalid={!!errors.last_name}
            />
          </Field>
          <Field label="Phone Cell" width={150}>
            <input
              value={form.phone_cell}
              onChange={update('phone_cell')}
              inputMode="tel"
              className="cell-mono"
            />
          </Field>
          <Field label="Phone Main" width={150}>
            <input
              value={form.phone_main}
              onChange={update('phone_main')}
              inputMode="tel"
              className="cell-mono"
            />
          </Field>
          <Field label="Email 1" width={220} error={errors.email1}>
            <input
              type="email"
              value={form.email1}
              onChange={update('email1')}
              className="cell-mono"
              aria-invalid={!!errors.email1}
            />
          </Field>
          <Field label="Position" width={200}>
            <input value={form.position} onChange={update('position')} />
          </Field>
          <Field label="Company" grow>
            <input value={form.company} onChange={update('company')} />
          </Field>

          {/* Row 2 — Where they are. */}
          <Field label="Address 1" grow>
            <input value={form.address1} onChange={update('address1')} />
          </Field>
          <Field label="Address 2" grow>
            <input value={form.address2} onChange={update('address2')} />
          </Field>
          <Field label="City" width={150}>
            <input value={form.city} onChange={update('city')} />
          </Field>
          <Field label="Province / State" width={80}>
            <input value={form.province_state} onChange={update('province_state')} />
          </Field>
          <Field label="Postal / Zip" width={100}>
            <input
              value={form.postal_zip}
              onChange={update('postal_zip')}
              className="cell-mono"
            />
          </Field>
          <Field label="Country" width={100}>
            <input value={form.country} onChange={update('country')} />
          </Field>

          {/* Row 3 — Secondary contact channels (rarely touched). */}
          <Field label="Phone Fax" width={150}>
            <input
              value={form.phone_fax}
              onChange={update('phone_fax')}
              inputMode="tel"
              className="cell-mono"
            />
          </Field>
          <Field label="Email 2" width={220} error={errors.email2}>
            <input
              type="email"
              value={form.email2}
              onChange={update('email2')}
              className="cell-mono"
              aria-invalid={!!errors.email2}
            />
          </Field>
          <Field label="Email 3" width={220} error={errors.email3}>
            <input
              type="email"
              value={form.email3}
              onChange={update('email3')}
              className="cell-mono"
              aria-invalid={!!errors.email3}
            />
          </Field>

          {/* Row 6 — Website (full width). */}
          <Field label="Website" grow>
            <input
              value={form.website}
              onChange={update('website')}
              placeholder="https://"
              className="cell-mono"
            />
          </Field>
          <Field label="First Contact" width={160}>
            {/* The date XSite first made contact with this account.
                Distinct from the DB-insert created_at. Backfilled by
                migrate-customer-first-contact-date.js from the earliest
                site install date; user can override here. For
                multi-site head offices like Centex Petroleum (where
                the parent record isn't itself a site) this is the
                only date the customer record carries. */}
            <input
              type="date"
              value={form.first_contact_date || ''}
              onChange={update('first_contact_date')}
            />
          </Field>
          <Field label="Status" width={140}>
            {/* Active/Inactive dropdown. Saving with Inactive runs the
                server-side cascade in customersController.update --
                every site under this customer gets sites.status =
                'Inactive', which is what the dashboard / renewal
                strip / month cards key off. Switching back to Active
                cascades the other way. */}
            <select value={form.current_status || ''} onChange={update('current_status')}>
              <option value="">—</option>
              {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              {/* Preserve any out-of-list value so a legacy record's
                  status isn't silently blanked on save. */}
              {form.current_status && !STATUS_OPTIONS.includes(form.current_status) && (
                <option value={form.current_status}>{form.current_status}</option>
              )}
            </select>
          </Field>
          <Field label="Highlight Color" width={170}>
            <select value={form.highlight_color || ''} onChange={update('highlight_color')}>
              <option value="">— None —</option>
              <option value="orange">Orange</option>
              <option value="red">Red</option>
              <option value="amber">Amber</option>
              <option value="yellow">Yellow</option>
              <option value="green">Green</option>
              <option value="blue">Blue</option>
              <option value="purple">Purple</option>
              <option value="grey">Grey</option>
            </select>
          </Field>
        </div>

        <div className="form-actions contact-card-actions">
          <span className="form-hint">Fields marked <span className="req">*</span> are required.</span>
          {onCancel && (
            <button type="button" className="btn-secondary" onClick={onCancel} disabled={saving}>
              Cancel
            </button>
          )}
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Saving…' : submitLabel}
          </button>
        </div>
      </div>
    </form>
  );
}

// Flex-wrap field. `width` (number, px) pins the field to that exact
// size; `grow` makes the field stretch to fill remaining row space.
// Omit both for a sensible default (220px). Inputs always fill 100% of
// the field width so the label-to-input alignment stays consistent.
function Field({ label, children, width, grow, required, error }) {
  const style = grow
    ? { flex: '1 1 200px', minWidth: 0 }
    : { flex: `0 0 ${width || 220}px`, width: width || 220, minWidth: 0 };
  return (
    <label
      className={`form-field${error ? ' form-field-error' : ''}`}
      style={style}
    >
      <span>
        {label}
        {required && <span className="req"> *</span>}
      </span>
      {children}
      {error && <span className="field-error">{error}</span>}
    </label>
  );
}
